import { useMutation, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountTransferStem,
  AssetCard,
  Button,
  Copyable,
  CurrencyAmountUI,
  cn,
  DataTable,
  DateUI,
  entryFromEventFragment,
  entryFromFragment,
  Heading,
  Icon,
  LabelPill,
  PrimaryFullScreenSpinner,
  PrimarySpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusUI,
  TimeLog,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
  toast,
  uppercaseStatusToStatus,
} from "@repo/ui";
import { NotFoundError, neverFunc, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { DateTime } from "luxon";
import { Suspense, useEffect, useMemo, useState } from "react";
import {
  PiBuildingOffice,
  PiPlus,
  PiShieldCheck,
  PiWarning,
} from "react-icons/pi";
import { RiExchangeDollarFill } from "react-icons/ri";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { PaneActions, TableAction } from "~/components/operations/actions";
import { FundMovementAddTransaction } from "~/components/operations/FundMovementAddTransaction";
import {
  NewPaymentInstructionFileAction,
  UpdatePaymentInstructionFileActions,
} from "~/components/PaymentInstructionFiles/Actions";
import { graphql } from "~/gql/client";
import type {
  PaymentInstructionFileKind,
  TransactionQueuePayoutFieldsFragment,
  TransactionQueueTransferFieldsFragment,
  TransactionsQueuePayoutPaneFragment,
  TransactionsQueueTransferPaneFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const tabs = [
  "upcoming",
  "ready-today",
  "ready-later",
  "in-progress",
  "recently-completed",
] as const;

type Tab = (typeof tabs)[number];

graphql(`
  fragment TransactionQueueTransferFields on Transfer {
    id
    requestedForDate
    updatedAt
    expectedProcessingDate
    transferStatus: status
    sourceAsset {
      id
      estimatedValue {
        amount
        currency
      }
      businessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      ...AssetCardFields
    }
    destinationAsset {
      id
      ...AssetCardFields
    }
    transferGroup {
      id
      kind
      transfers {
        id
      }
      payouts {
          id
      }
    }
    currencyAmount {
      amount
      currency
    }
    eligiblePaymentInstructionFileKind
    pendingPaymentInstructionFile {
      id
      shortRef
      kind
      url
    }
    ...OperationsTransferActions
  }

  fragment TransactionQueuePayoutFields on Payout {
    id
    requestedForDate
    updatedAt
    expectedProcessingDate
    status
    amount {
      amount
      currency
    }
    businessEntity {
      id
      name
      organisation {
        id
        name
        squareLogoUrl
      }
    }
    sourceAsset {
      id
      estimatedValue {
        amount
        currency
      }
      ...AssetCardFields
    }
    beneficiaryAccount {
      id
      ...BeneficiaryAssetCardFields
    }
    eligiblePaymentInstructionFileKind
    pendingPaymentInstructionFile {
      id
      shortRef
      kind
      url
    }
    transferGroup {
        id
        kind
        payouts {
            id
        }
        transfers {
            id
        }
    }
    ...OperationsPayoutActions
  }

  fragment TransactionsQueueTransferPane on Transfer {
    id
    requestedForDate
    description
    updatedAt
    paymentReference
    expectedProcessingDate
    transferStatus: status
    sourceAsset {
      id
      estimatedValue {
        amount
        currency
      }
      businessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      ...AssetCardFields
    }
    destinationAsset {
      id
      ...AssetCardFields
    }
    transferGroup {
      id
      kind
      transfers {
        id
        currencyAmount {
          currency
          amount
        }
        status
        shortRef
        sourceAsset {
          id
          ...AssetCardFields
        }
      }
      payouts {
          id
      }
    }
    currencyAmount {
      amount
      currency
    }
    approvalRequest {
      id
      approvalPolicy {
        id
        numUserReviewsRequired
      }
      currentApprovedCount
      result
    }
    ...OperationsTransferActions
    ...AddTransferTransactionDialogFields
    executionMethod
    events {
      ...TimeLogEventEntry
    }
  }

  fragment TransactionsQueuePayoutPane on Payout {
    id
    requestedForDate
    status
    expectedProcessingDate
    paymentReference
    updatedAt
    reference
    canUpdateSourceAsset
    amount {
      amount
      currency
    }
    businessEntity {
      id
      name
      organisation {
        id
        name
        squareLogoUrl
      }
      assets {
        id
        ...AssetCardFields
        estimatedValue {
          amount
          currency
        }
      }
    }
    sourceAsset {
      id
      estimatedValue {
        amount
        currency
      }
      ...AssetCardFields
    }
    beneficiaryAccount {
      id
      ...BeneficiaryAssetCardFields
    }
    updates {
      ...TimeLogEntry
    }
    events {
      ...TimeLogEventEntry
    }
    ...OperationsPayoutActions
    ...AddPayoutTransactionDialogFields
    executionMethod
    transferGroup {
        id
        kind
        transfers {
            id
        }
        payouts {
            id
        }
    }
    approvalRequest {
      id
      approvalPolicy {
        id
        numUserReviewsRequired
      }
      currentApprovedCount
      result
    }
  }
`);

type Item =
  | TransactionQueuePayoutFieldsFragment
  | TransactionQueueTransferFieldsFragment;

type ItemGroup = {
  title: string;
  items: Item[];
} & (
  | {
      actionKind: "PENDING_FILE";
      fileKind: PaymentInstructionFileKind;
      fileId: string;
      fileUrl: string | undefined;
    }
  | {
      actionKind: "NEW_FILE";
      fileKind: PaymentInstructionFileKind;
    }
  | {
      actionKind: "NONE";
    }
);

const View = () => {
  const params = useParams({ strict: false });

  let selectedTab: Tab | undefined;
  if (tabs.includes(params["tab"] as Tab)) {
    selectedTab = params["tab"] as Tab;
  } else {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransactionsQueue {
        transactionQueue {
          readyForProcessingLater {
            id
            count
            items {
              ...TransactionQueuePayoutFields
              ...TransactionQueueTransferFields
            }
          }
          readyForProcessingToday {
            id
            count
            items {
              ...TransactionQueuePayoutFields
              ...TransactionQueueTransferFields
            }
          }
          upcoming {
            id
            count
            items {
              ...TransactionQueuePayoutFields
              ...TransactionQueueTransferFields
            }
          }
          inProgress {
            id
            count
            items {
              ...TransactionQueuePayoutFields
              ...TransactionQueueTransferFields
            }
          }
          recentlyCompleted {
            id
            count
            items {
              ...TransactionQueuePayoutFields
              ...TransactionQueueTransferFields
            }
          }
        }
      }
    `),
  );

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [sidePaneVisible, setSidePaneVisible] = useState<boolean>(false);

  useEffect(() => {
    if (selectedTab) {
      setSidePaneVisible(false);
    }
  }, [selectedTab]);

  const itemGroups = useMemo(() => {
    let listItems;
    switch (selectedTab) {
      case "upcoming":
        listItems = data.transactionQueue?.upcoming?.items ?? [];
        break;
      case "ready-today":
        listItems = data.transactionQueue?.readyForProcessingToday?.items ?? [];
        break;
      case "ready-later":
        listItems = data.transactionQueue?.readyForProcessingLater?.items ?? [];
        break;
      case "in-progress":
        listItems = data.transactionQueue?.inProgress?.items ?? [];
        break;
      case "recently-completed":
        listItems = data.transactionQueue?.recentlyCompleted?.items ?? [];
        break;
      default:
        return neverFunc(selectedTab);
    }
    listItems = listItems.filter((li) => li.__typename !== "EburyFXTrade");

    if (selectedTab !== "ready-today") {
      return [
        {
          title: "Transactions",
          items: (listItems ?? []).filter((t) => t.__typename !== "Deposit"),
          actionKind: "NONE",
        },
      ] satisfies ItemGroup[];
    }

    const main: Item[] = [];
    const cmaEgress: Item[] = [];
    const acceleratorEgress: Item[] = [];
    const perpetualBundle: Item[] = [];
    const pendingFiles = new Map<
      string,
      ItemGroup & { actionKind: "PENDING_FILE" }
    >();

    for (const item of (listItems ?? []).filter(
      (t) => t.__typename !== "Deposit",
    )) {
      if (item.eligiblePaymentInstructionFileKind === "MACQUARIE_CMA_EGRESS") {
        cmaEgress.push(item);
        continue;
      }
      if (
        item.eligiblePaymentInstructionFileKind ===
        "MACQUARIE_CMA_ACCELERATOR_EGRESS"
      ) {
        acceleratorEgress.push(item);
        continue;
      }
      if (item.eligiblePaymentInstructionFileKind === "PERPETUAL_BUNDLE") {
        perpetualBundle.push(item);
        continue;
      }
      if (item.pendingPaymentInstructionFile) {
        let fileGroup = pendingFiles.get(item.pendingPaymentInstructionFile.id);
        if (!fileGroup) {
          const pendingFileTitle =
            item.pendingPaymentInstructionFile.kind === "MACQUARIE_CMA_EGRESS"
              ? `Pending Batch file: Macquarie CMA Egress #${item.pendingPaymentInstructionFile.shortRef}`
              : item.pendingPaymentInstructionFile.kind === "PERPETUAL_BUNDLE"
                ? `Pending Batch file: Perpetual Bundle #${item.pendingPaymentInstructionFile.shortRef}`
                : `Pending Batch file: Macquarie CMA Accelerator to Nominated account #${item.pendingPaymentInstructionFile.shortRef}`;
          fileGroup = {
            title: pendingFileTitle,
            actionKind: "PENDING_FILE",
            fileId: item.pendingPaymentInstructionFile.id,
            fileKind: item.pendingPaymentInstructionFile.kind,
            fileUrl: item.pendingPaymentInstructionFile.url ?? undefined,
            items: [],
          };
        }
        fileGroup.items.push(item);
        pendingFiles.set(item.pendingPaymentInstructionFile.id, fileGroup);
        continue;
      }
      main.push(item);
    }

    const itemGroups = (
      [
        ...Array.from(pendingFiles.values()),
        {
          title: "Batch file: Macquarie CMA Egress",
          items: cmaEgress,
          actionKind: "NEW_FILE",
          fileKind: "MACQUARIE_CMA_EGRESS",
        },
        {
          title: "Batch file: Macquarie CMA Accelerator to Nominated account",
          items: acceleratorEgress,
          actionKind: "NEW_FILE",
          fileKind: "MACQUARIE_CMA_ACCELERATOR_EGRESS",
        },
        {
          title: "Batch file: Perpetual Bundle",
          items: perpetualBundle,
          actionKind: "NEW_FILE",
          fileKind: "PERPETUAL_BUNDLE",
        },
        {
          title: "Individual transactions",
          items: main,
          actionKind: "NONE",
        },
      ] satisfies ItemGroup[]
    ).filter((g) => g.items.length > 0);

    if (itemGroups.length === 0) {
      itemGroups.push({
        title: "Transactions",
        items: [],
        actionKind: "NONE",
      });
    }

    return itemGroups;
  }, [selectedTab, data.transactionQueue]);

  const tabColumns = useMemo(() => {
    switch (selectedTab) {
      case "ready-today":
        return columns;
      case "upcoming":
      case "ready-later":
        return columns.filter((c) => c.id !== "action");
      case "in-progress":
      case "recently-completed":
        return columns.filter(
          (c) => !["funds_available"].includes(String(c.id)),
        );
      default:
        return neverFunc(selectedTab);
    }
  }, [selectedTab]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSidePaneVisible(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="m-4">
      <AdminPageHeader title="Transaction Queue" className="ml-4" />
      <div className="flex items-start gap-2">
        <div className="grid w-full flex-1 gap-6" style={{ zIndex: 1 }}>
          <div className="flex justify-start gap-4">
            <OpsTab
              label="Upcoming"
              badgeNumber={data.transactionQueue?.upcoming?.count}
              to="upcoming"
            />
            <OpsTab
              label="Ready today"
              badgeNumber={
                data.transactionQueue?.readyForProcessingToday?.count
              }
              to="ready-today"
            />
            <OpsTab
              label="Ready later"
              badgeNumber={
                data.transactionQueue?.readyForProcessingLater?.count
              }
              to="ready-later"
            />
            <OpsTab
              label="In progress"
              badgeNumber={data.transactionQueue?.inProgress?.count}
              to="in-progress"
            />
            <OpsTab
              label="Recently Completed"
              badgeNumber={data.transactionQueue?.recentlyCompleted?.count}
              to="recently-completed"
            />
          </div>

          {itemGroups.map((group) => {
            return (
              <div
                key={group.title}
                className="flex w-full flex-col gap-6 rounded-xl border border-zinc-100 bg-white px-6 pt-6 pb-6 shadow-md"
              >
                <div className="flex justify-between">
                  <div className="font-semibold text-lg">{group.title}</div>
                  {group.actionKind === "NEW_FILE" && (
                    <NewPaymentInstructionFileAction
                      kind={group.fileKind}
                      fundMovementIds={group.items.map(({ id }) => id)}
                    />
                  )}
                  {group.actionKind === "PENDING_FILE" && (
                    <UpdatePaymentInstructionFileActions
                      id={group.fileId}
                      url={group.fileUrl}
                    />
                  )}
                </div>

                <DataTable
                  data={group.items}
                  activeRowId={sidePaneVisible ? selectedId : undefined}
                  hidePagination={true}
                  pageSize={10_000}
                  onRowClick={(row) => {
                    setSelectedId(row.original.id);
                    setSidePaneVisible(
                      sidePaneVisible ? row.original.id !== selectedId : true,
                    );
                  }}
                  cellClassName="py-1 px-4"
                  columns={tabColumns}
                />
              </div>
            );
          })}
        </div>

        <div
          className={cn(
            "absolute top-0 right-0 h-full w-1/2 transform overflow-auto border-l bg-white p-8 shadow-elevate-drop-shadow duration-300 ease-in-out",
            sidePaneVisible
              ? "translate-x-0"
              : "pointer-events-none translate-x-full",
          )}
          style={{
            zIndex: 1,
          }}
        >
          {selectedId && (
            <FundMovementPane
              onClose={() => setSidePaneVisible(false)}
              fundMovementId={selectedId}
            />
          )}
        </div>
        {sidePaneVisible && (
          <div
            className="absolute top-0 left-0 h-full w-full overflow-hidden"
            style={{
              zIndex: 0,
            }}
            onClick={() => {
              setSidePaneVisible(false);
            }}
          ></div>
        )}
      </div>
    </div>
  );
};

const columns: ColumnDef<
  TransactionQueuePayoutFieldsFragment | TransactionQueueTransferFieldsFragment
>[] = [
  {
    header: "Requested",
    cell: ({ row }) => (
      <DateUI date={row.original.requestedForDate} variant="DAY_MONTH" />
    ),
  },
  {
    header: "Client",
    cell: ({ row }) => {
      const be =
        row.original.__typename === "Transfer"
          ? row.original.sourceAsset?.businessEntity
          : row.original.businessEntity;

      return (
        <div className="flex flex-row items-center gap-1">
          <Link
            to={`/organisations/${be?.organisation?.id}/business-entities/${be?.id}`}
          >
            <Icon
              size="8"
              imageUrl={be?.organisation?.squareLogoUrl ?? undefined}
              fallbackIcon={<PiBuildingOffice />}
            />
          </Link>
          <div className="flex w-fit flex-col">
            <Txt
              weight="semibold"
              size="sm"
              as="div"
              className="max-w-36 truncate"
            >
              {be?.name}
            </Txt>
            <Txt
              color="secondary"
              size="sm"
              as="div"
              className="max-w-36 truncate"
            >
              {be?.organisation?.name}
            </Txt>
          </div>
        </div>
      );
    },
  },
  {
    header: "Purpose",
    cell: ({ row }) => {
      let label = "";
      switch (row.original.__typename) {
        case "Transfer": {
          if (row.original.transferGroup) {
            label = `${prettyPrintEnum(row.original.transferGroup.kind)} ${
              (row.original.transferGroup.transfers ?? []).findIndex(
                (t) => t.id === row.original.id,
              ) + 1
            }/${(row.original.transferGroup.transfers?.length ?? 0) + (row.original.transferGroup.payouts?.length ?? 0)}`;
          } else {
            label = "Transfer";
          }
          break;
        }
        case "Payout": {
          if (row.original.transferGroup) {
            label = `${prettyPrintEnum(row.original.transferGroup.kind)} ${
              (row.original.transferGroup.payouts ?? []).findIndex(
                (t) => t.id === row.original.id,
              ) +
              1 +
              (row.original.transferGroup.transfers?.length ?? 0)
            }/${(row.original.transferGroup.transfers?.length ?? 0) + (row.original.transferGroup.payouts?.length ?? 0)}`;
          } else {
            label = "Payout";
          }
        }
      }

      return <LabelPill className="capitalize" size="md" label={label} />;
    },
  },
  {
    header: "Source account",
    cell: ({ row }) =>
      row.original.sourceAsset ? (
        <AssetCard variant="IDENTIFIER" asset={row.original.sourceAsset} />
      ) : null,
  },
  {
    header: "Destination account",
    cell: ({ row }) => {
      if (
        row.original.__typename === "Transfer" &&
        row.original.sourceAsset &&
        row.original.destinationAsset
      ) {
        return (
          <AssetCard
            variant="IDENTIFIER"
            asset={row.original.destinationAsset}
          />
        );
      } else if (
        row.original.__typename === "Payout" &&
        row.original.beneficiaryAccount
      ) {
        return (
          <AssetCard
            variant="IDENTIFIER"
            beneficiaryAccount={row.original.beneficiaryAccount}
            hidePayoutsOnlyTooltip
          />
        );
      }
      return null;
    },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => {
      const status =
        row.original.__typename === "Transfer"
          ? row.original.transferStatus
          : row.original.status;

      return (
        status && (
          <Tooltip delayDuration={250}>
            <TooltipTrigger>
              <StatusUI
                status={uppercaseStatusToStatus[status]}
                className="flex-shrink"
              />
            </TooltipTrigger>
            <TooltipContent
              arrowPadding={0}
              className="border-none bg-zinc-700 px-2 py-1 font-text text-sm text-white "
            >
              {DateTime.fromJSDate(row.original.updatedAt).toLocaleString(
                DateTime.DATETIME_MED,
              )}
            </TooltipContent>
          </Tooltip>
        )
      );
    },
  },
  {
    id: "amount",
    header: () => <div className="text-end">Amount</div>,
    cell: ({ row }) => {
      const amount =
        row.original.__typename === "Transfer"
          ? row.original.currencyAmount
          : row.original.amount;
      return (
        amount && (
          <div className="text-end">
            <CurrencyAmountUI
              currency={amount.currency}
              amount={amount.amount}
            />
          </div>
        )
      );
    },
  },
  {
    id: "funds_available",
    header: () => <div className="text-center">Funds available</div>,
    cell: ({ row }) => {
      const sourceAccountBalance = row.original.sourceAsset?.estimatedValue;
      const amount =
        row.original.__typename === "Transfer"
          ? row.original.currencyAmount
          : row.original.amount;
      if (
        sourceAccountBalance &&
        amount &&
        sourceAccountBalance.currency === amount.currency
      ) {
        let content = <PiWarning className="size-6 text-warning-500" />;
        if (sourceAccountBalance.amount.gte(amount.amount)) {
          content = <PiShieldCheck className="size-6 text-success-600" />;
        }
        return (
          <div className="flex w-full items-center justify-center self-center">
            <Tooltip delayDuration={250}>
              <TooltipTrigger>{content}</TooltipTrigger>
              <TooltipContent
                arrowPadding={0}
                className="border-none bg-zinc-700 px-2 py-1 font-text text-sm text-white "
              >
                <CurrencyAmountUI
                  currency={amount.currency}
                  amount={amount.amount}
                />
              </TooltipContent>
            </Tooltip>
          </div>
        );
      }
      return null;
    },
  },
  {
    id: "action",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      return <TableAction {...row.original} />;
    },
  },
];

function OpsTab({
  to,
  label,
  badgeNumber,
}: {
  to: Tab;
  label: React.ReactNode;
  badgeNumber?: number | null;
}) {
  return (
    <Link
      to={`/transactions-queue/${to}`}
      activeProps={{ className: "active" }}
      className={cn(
        "group grid grid-flow-row place-items-start gap-2 whitespace-nowrap rounded-lg p-4 ring-offset-white ",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "min-w-40 max-w-60",
        "disabled:pointer-events-none disabled:opacity-50",
        "font-normal text-secondary",
        "border border-transparent",
        "[&.active]:bg-elevation-sunken-1 [&.active]:font-medium [&.active]:text-primary [&.active]:shadow-elevate-drop-shadow",
        "hover:bg-zinc-50 hover:shadow-md",
        "cursor-pointer",
      )}
    >
      <span className="text-sm text-tertiary group-[&.active]:text-inherit">
        {label}
      </span>
      {badgeNumber !== undefined && (
        <span className="text-lg">{badgeNumber}</span>
      )}
    </Link>
  );
}

function FundMovementPane({
  fundMovementId,
  onClose,
}: {
  fundMovementId: string;
  onClose: () => void;
}) {
  const { data, loading } = useQuery(
    graphql(`
      query AdminTransactionsQueuePane($id: ID!) {
        node(id: $id) {
          ...TransactionsQueueTransferPane
          ...TransactionsQueuePayoutPane
        }
      }
    `),
    {
      variables: { id: fundMovementId },
      fetchPolicy: "cache-first",
    },
  );
  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center opacity-10">
        <PrimarySpinner />
      </div>
    );
  }
  if (!data) {
    throw new Error(`expected ${fundMovementId}`);
  }

  switch (data.node?.__typename) {
    case "Payout":
      return <PayoutPane onClose={onClose} payout={data.node} />;
    case "Transfer":
      return <TransferPane onClose={onClose} transfer={data.node} />;
    default:
      throw new Error(`unexpected node ${data.node?.__typename}`);
  }
}

function TransferPane({
  transfer,
  onClose,
}: {
  transfer: TransactionsQueueTransferPaneFragment;
  onClose: () => void;
}) {
  const be = transfer.sourceAsset?.businessEntity;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="flex flex-row justify-between">
          <Heading as="h1" size="2xl" className="items-center">
            Transfer{" "}
            <Link to={`/transfers/${transfer.id}`}>
              <span className="font-mono text-2xl">
                {transfer.paymentReference}
              </span>
            </Link>
            <div>
              {transfer.transferStatus && (
                <Tooltip delayDuration={250}>
                  <TooltipTrigger>
                    <StatusUI
                      status={uppercaseStatusToStatus[transfer.transferStatus]}
                      className="flex-shrink"
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    arrowPadding={0}
                    className="border-none bg-zinc-700 px-2 py-1 font-text text-sm text-white "
                  >
                    {DateTime.fromJSDate(transfer.updatedAt).toLocaleString(
                      DateTime.DATETIME_MED,
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </Heading>
          <Button
            variant="secondary"
            size="icon-sm"
            className="top-2 right-2"
            onClick={onClose}
          >
            <X className="m-1 size-full" />
          </Button>
        </div>
        <div className="flex flex-row gap-2">
          <Link to={`/organisations/${be?.organisation?.id}`}>
            <Txt size="sm" as="span" className="truncate hover:underline">
              {be?.organisation?.name}
            </Txt>
          </Link>
          ·
          <Link
            to={`/organisations/${be?.organisation?.id}/business-entities/${be?.id}`}
          >
            <Txt size="sm" as="span" className="truncate hover:underline">
              {be?.name}
            </Txt>
          </Link>
        </div>
      </div>

      <Txt
        font="title"
        weight="medium"
        size="lg"
        className="my-4 w-full border-b border-b-primary"
      >
        Actions
      </Txt>
      <PaneActions {...transfer} />

      <div className="my-4 flex w-full flex-full items-end justify-between border-b border-b-primary">
        <Txt font="title" weight="medium" size="lg">
          Details
        </Txt>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex w-full flex-col justify-between gap-2">
          {transfer.sourceAsset && (
            <div className="flex w-[85%] flex-row items-center justify-between rounded-md border border-blue-200 bg-blue-50 p-4">
              <AssetCard
                variant="IDENTIFIER"
                asset={transfer.sourceAsset}
                linkTo={`/assets/${transfer.sourceAsset.id}`}
              />
              <div className="flex flex-row items-center gap-2">
                {transfer.sourceAsset?.estimatedValue && (
                  <div className="flex flex-col items-end justify-center">
                    <Txt
                      className="whitespace-nowrap"
                      as="div"
                      size="xs"
                      color="secondary"
                    >
                      Account balance
                    </Txt>
                    <CurrencyAmountUI
                      size="sm"
                      currency={transfer.sourceAsset.estimatedValue.currency}
                      amount={transfer.sourceAsset.estimatedValue.amount}
                    />
                  </div>
                )}
                <FundMovementAddTransaction
                  createTrigger={
                    <div className="ml-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-icon-primary-inverse hover:border hover:border-slate-200">
                      <PiPlus />
                    </div>
                  }
                  transactionType="TRANSFER_OUT"
                  {...transfer}
                />
              </div>
            </div>
          )}

          <AccountTransferStem iconComponent={RiExchangeDollarFill}>
            <div className="flex min-h-16 flex-col justify-center pb-4">
              {transfer.currencyAmount && (
                <CurrencyAmountUI
                  size="xl"
                  currency={transfer.currencyAmount.currency}
                  amount={transfer.currencyAmount.amount}
                />
              )}
            </div>
          </AccountTransferStem>

          {transfer.destinationAsset && (
            <div className="flex w-[85%] flex-row items-center justify-between rounded-md border border-blue-200 bg-blue-50 p-4">
              <AssetCard
                variant="IDENTIFIER"
                asset={transfer.destinationAsset}
                linkTo={`/assets/${transfer.destinationAsset.id}`}
              />
              <div className="flex flex-row items-center gap-2">
                {transfer.destinationAsset.estimatedValue && (
                  <div className="flex flex-col items-end justify-center">
                    <Txt
                      className="whitespace-nowrap"
                      as="div"
                      size="xs"
                      color="secondary"
                    >
                      Account balance
                    </Txt>
                    <CurrencyAmountUI
                      size="sm"
                      currency={
                        transfer.destinationAsset.estimatedValue.currency
                      }
                      amount={transfer.destinationAsset.estimatedValue.amount}
                    />
                  </div>
                )}

                <FundMovementAddTransaction
                  createTrigger={
                    <div className="ml-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-icon-primary-inverse hover:border hover:border-slate-200">
                      <PiPlus />
                    </div>
                  }
                  transactionType="TRANSFER_IN"
                  {...transfer}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row gap-16">
          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Requested at
            </Txt>
            <Txt size="sm">
              <DateUI date={transfer.requestedForDate} />
            </Txt>
          </div>

          {transfer.expectedProcessingDate && (
            <div className="flex flex-col">
              <Txt size="base" as="div" weight="medium">
                Processing (est)
              </Txt>
              <Txt size="sm">
                <DateUI date={transfer.expectedProcessingDate} />
              </Txt>
            </div>
          )}

          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Payment reference
            </Txt>
            <Txt size="sm" as="div">
              <Copyable>{transfer.paymentReference}</Copyable>
            </Txt>
          </div>
        </div>

        <div>
          <Txt size="base" as="div" weight="medium">
            Description
          </Txt>
          <Txt as="div" size="sm">
            {transfer.description}
          </Txt>
        </div>

        {transfer.transferGroup && (
          <div>
            <Txt
              as="div"
              size="base"
              weight="medium"
              className="mb-2 capitalize"
            >
              Transfer group ({prettyPrintEnum(transfer.transferGroup.kind)})
            </Txt>
            <div className="flex flex-col gap-4">
              <Txt>
                Transfer #
                {(transfer.transferGroup.transfers ?? []).findIndex(
                  (t) => t.id === transfer.id,
                ) + 1}{" "}
                of{" "}
                {(transfer.transferGroup.transfers?.length ?? 0) +
                  (transfer.transferGroup.payouts?.length ?? 0)}
                . Transfer group details can be found{" "}
                <Link
                  className="underline"
                  to={`/transfer-groups/${transfer.transferGroup.id}`}
                >
                  here
                </Link>
                .
              </Txt>
            </div>
          </div>
        )}

        {transfer.approvalRequest &&
          transfer.approvalRequest.approvalPolicy &&
          transfer.approvalRequest.approvalPolicy.numUserReviewsRequired >
            0 && (
            <div>
              <Txt size="base" as="div" weight="medium">
                Approvals
              </Txt>
              <Txt as="div" size="sm">
                Received{" "}
                <Txt as="span" weight="semibold">
                  {transfer.approvalRequest.currentApprovedCount}
                </Txt>{" "}
                of{" "}
                <Txt as="span" weight="semibold">
                  {
                    transfer.approvalRequest.approvalPolicy
                      .numUserReviewsRequired
                  }
                </Txt>{" "}
                required.
              </Txt>
            </div>
          )}

        {transfer.events && (
          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Event log
            </Txt>
            <TimeLog entries={transfer.events.map(entryFromEventFragment)} />
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutPane({
  payout,
  onClose,
}: {
  payout: TransactionsQueuePayoutPaneFragment;
  onClose: () => void;
}) {
  const be = payout.businessEntity;
  const sourceAsset = payout.sourceAsset;
  const sourceAssetOptions = (be?.assets ?? []).filter(
    (asset) => asset.currency === payout.amount.currency,
  );

  const [updateSourceAsset] = useMutation(
    graphql(`
      mutation UpdatePayoutSourceAssetTransactionQueue($payoutId: ID!, $sourceAssetId: ID!) {
          updatePayout(input: {
             id: $payoutId,
             sourceAssetId: $sourceAssetId
          }) {
              payout {
                  id
                  sourceAsset {
                      id
                      estimatedValue {
                        currency
                        amount
                      }
                  }
              }
          }
      }
  `),
    {
      onError() {
        toast({
          title: "Something went wrong",
          description: "Please refresh the page and try again",
          kind: "error",
        });
      },
    },
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col">
        <div className="flex flex-row justify-between">
          <Heading as="h1" size="2xl" className="items-center">
            Payout{" "}
            <Link to={`/payouts/${payout.id}`}>
              <span className="font-mono text-2xl">
                {payout.paymentReference}
              </span>
            </Link>
            <div>
              {payout.status && (
                <Tooltip delayDuration={250}>
                  <TooltipTrigger>
                    <StatusUI
                      status={uppercaseStatusToStatus[payout.status]}
                      className="flex-shrink"
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    arrowPadding={0}
                    className="border-none bg-zinc-700 px-2 py-1 font-text text-sm text-white "
                  >
                    {DateTime.fromJSDate(payout.updatedAt).toLocaleString(
                      DateTime.DATETIME_MED,
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </Heading>
          <Button
            variant="secondary"
            size="icon-sm"
            className="top-2 right-2"
            onClick={onClose}
          >
            <X className="m-1 size-full" />
          </Button>
        </div>

        <div className="flex flex-row gap-2">
          <Link to={`/organisations/${be?.organisation?.id}`}>
            <Txt size="sm" as="span" className="truncate hover:underline">
              {be?.organisation?.name}
            </Txt>
          </Link>
          ·
          <Link
            to={`/organisations/${be?.organisation?.id}/business-entities/${be?.id}`}
          >
            <Txt size="sm" as="span" className="truncate hover:underline">
              {be?.name}
            </Txt>
          </Link>
        </div>
      </div>

      <Txt
        font="title"
        weight="medium"
        size="lg"
        className="my-4 w-full border-b border-b-primary"
      >
        Actions
      </Txt>

      <PaneActions {...payout} />

      <div className="my-4 flex w-full flex-full items-end justify-between border-b border-b-primary">
        <Txt font="title" weight="medium" size="lg">
          Details
        </Txt>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex w-full flex-col justify-between gap-2">
          <div className="flex w-[85%] flex-row items-center justify-between rounded-md border border-blue-200 bg-blue-50 p-4">
            {payout.canUpdateSourceAsset ? (
              <Select
                name="sourceAssetId"
                defaultValue={sourceAsset?.id}
                onValueChange={(v) => {
                  const newSourceAsset = sourceAssetOptions.find(
                    (o) => o.id === v,
                  );
                  if (!newSourceAsset) {
                    throw new Error("expected source asset");
                  }
                  updateSourceAsset({
                    variables: {
                      payoutId: payout.id,
                      sourceAssetId: newSourceAsset.id,
                    },
                    optimisticResponse: {
                      __typename: "Mutation",
                      updatePayout: {
                        __typename: "UpdatePayoutPayload",
                        payout: {
                          __typename: "Payout",
                          id: payout.id,
                          sourceAsset: {
                            __typename: newSourceAsset.__typename,
                            id: newSourceAsset.id,
                            estimatedValue: newSourceAsset.estimatedValue,
                          },
                        },
                      },
                    },
                  });
                }}
              >
                <SelectTrigger className="h-auto w-fit min-w-[30ch] text-start">
                  <div className="flex items-center gap-4 pe-4">
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {[...sourceAssetOptions]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((a) => (
                      <SelectItem value={a.id} key={a.id}>
                        <AssetCard variant="IDENTIFIER" asset={a} />
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              sourceAsset && (
                <AssetCard variant="IDENTIFIER" asset={sourceAsset} />
              )
            )}

            <div className="flex flex-row items-center gap-2">
              {sourceAsset?.estimatedValue && (
                <div className="flex flex-col items-end justify-center">
                  <Txt
                    className="whitespace-nowrap"
                    as="div"
                    size="xs"
                    color="secondary"
                  >
                    Account balance
                  </Txt>
                  <CurrencyAmountUI
                    size="sm"
                    currency={sourceAsset.estimatedValue.currency}
                    amount={sourceAsset.estimatedValue.amount}
                  />
                </div>
              )}
              {sourceAsset && (
                <FundMovementAddTransaction
                  createTrigger={
                    <div className="ml-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-icon-primary-inverse hover:border hover:border-slate-200">
                      <PiPlus />
                    </div>
                  }
                  transactionType="PAYOUT"
                  {...payout}
                />
              )}
            </div>
          </div>

          <AccountTransferStem iconComponent={RiExchangeDollarFill}>
            <div className="flex min-h-16 flex-col justify-center pb-4">
              {payout.amount && (
                <CurrencyAmountUI
                  size="xl"
                  currency={payout.amount.currency}
                  amount={payout.amount.amount}
                />
              )}
            </div>
          </AccountTransferStem>

          {payout.beneficiaryAccount && (
            <div className="flex w-[85%] flex-row items-center justify-between rounded-md border border-blue-200 bg-blue-50 p-4">
              <AssetCard
                variant="IDENTIFIER"
                beneficiaryAccount={payout.beneficiaryAccount}
                linkTo={`/beneficiary-accounts/${payout.beneficiaryAccount.id}`}
              />
            </div>
          )}
        </div>

        <div className="flex flex-row gap-16">
          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Requested for
            </Txt>
            <Txt size="sm">
              <DateUI date={payout.requestedForDate} />
            </Txt>
          </div>

          {payout.expectedProcessingDate && (
            <div className="flex flex-col">
              <Txt size="base" as="div" weight="medium">
                Processing (est)
              </Txt>
              <Txt size="sm">
                <DateUI date={payout.expectedProcessingDate} />
              </Txt>
            </div>
          )}

          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Payment reference
            </Txt>
            <Txt size="sm" as="div">
              <Copyable>{payout.paymentReference}</Copyable>
            </Txt>
          </div>
        </div>

        {payout.reference && (
          <div>
            <Txt size="base" as="div" weight="medium">
              Reference
            </Txt>
            <Txt as="div" size="sm">
              {payout.reference}
            </Txt>
          </div>
        )}

        {payout.transferGroup && (
          <div>
            <Txt
              as="div"
              size="base"
              weight="medium"
              className="mb-2 capitalize"
            >
              Transfer group ({prettyPrintEnum(payout.transferGroup.kind)})
            </Txt>
            <div className="flex flex-col gap-4">
              <Txt>
                Payout #
                {(payout.transferGroup.payouts ?? []).findIndex(
                  (t) => t.id === payout.id,
                ) +
                  1 +
                  (payout.transferGroup.transfers?.length ?? 0)}{" "}
                of{" "}
                {(payout.transferGroup.transfers?.length ?? 0) +
                  (payout.transferGroup.payouts?.length ?? 0)}
                . Transfer group details can be found{" "}
                <Link
                  className="underline"
                  to={`/transfer-groups/${payout.transferGroup.id}`}
                >
                  here
                </Link>
                .
              </Txt>
            </div>
          </div>
        )}

        {payout.approvalRequest &&
          payout.approvalRequest.approvalPolicy &&
          payout.approvalRequest.approvalPolicy.numUserReviewsRequired > 0 && (
            <div>
              <Txt size="base" as="div" weight="medium">
                Approvals
              </Txt>
              <Txt as="div" size="sm">
                Received{" "}
                <Txt as="span" weight="semibold">
                  {payout.approvalRequest.currentApprovedCount}
                </Txt>{" "}
                of{" "}
                <Txt as="span" weight="semibold">
                  {payout.approvalRequest.approvalPolicy.numUserReviewsRequired}
                </Txt>{" "}
                required.
              </Txt>
            </div>
          )}

        {payout.updates && (
          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Timeline
            </Txt>
            <TimeLog entries={payout.updates.map(entryFromFragment)} />
          </div>
        )}
        {payout.events && (
          <div className="flex flex-col">
            <Txt size="base" as="div" weight="medium">
              Event log
            </Txt>
            <TimeLog entries={payout.events.map(entryFromEventFragment)} />
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/__auth/transactions-queue/$tab")({
  head: () => opsHead("Transactions Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});
